import {
  adaptStoreToFilterPath,
  adaptStoreToPageURL,
} from 'src/adapters/storeToPageURL/adaptStoreToPageURL';
import { deriveTaxonomyLevelsFromFacets } from 'src/adapters/storeToPageURL/deriveTaxonomyLevelsFromFacets';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { TriggerName } from 'src/redux/products/types';
import type { SyncProductsQueryFromURLPayload } from '../types/ProductsQueryState';
import { getProductsQueryStateFromSyncPayload } from './getProductsQueryStateFromSyncPayload';

const sortAndPageFromState = (state: ReduxApplicationState, productsQuery = state.productsQuery) =>
  adaptStoreToPageURL({ ...state, productsQuery }, productsQuery.page);

// Signature that distinguishes both `/-/...` filters and taxonomy-level selections. Works
// uniformly for taxonomy and non-taxonomy pages — taxonomy levels collapse to `[]` when
// `taxonomyAttributes` is empty, so the suffix is a constant for non-taxonomy state.
const filterSignature = (productsQuery: ReduxApplicationState['productsQuery']): string => {
  const filterPath = adaptStoreToFilterPath({ productsQuery });
  const levels = deriveTaxonomyLevelsFromFacets(
    productsQuery.facets,
    productsQuery.taxonomyAttributes,
  );
  return `${filterPath}|${JSON.stringify(levels)}`;
};

export const getRouteChangeTriggerName = (
  state: ReduxApplicationState,
  nextActive: SyncProductsQueryFromURLPayload,
): TriggerName => {
  const currentPq = state.productsQuery;
  const nextPq = getProductsQueryStateFromSyncPayload(currentPq, nextActive);

  const current = sortAndPageFromState(state, currentPq);
  const next = sortAndPageFromState(state, nextPq);

  const sortChanged = current.get('sort') !== next.get('sort');
  const pageChanged = current.get('page') !== next.get('page');
  const filterChanged = filterSignature(currentPq) !== filterSignature(nextPq);

  if (pageChanged && !filterChanged && !sortChanged) {
    return TriggerName.PAGE_NUMBER_CHANGE;
  }
  if (sortChanged && !filterChanged) {
    return TriggerName.CHANGE_SORT;
  }
  return TriggerName.CHANGE_FACETS;
};
